import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from './api.service';

@Component({
  selector: 'app-purchase-orders',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="pb-24 lg:pb-8 min-h-screen lg:min-h-[calc(100vh-72px)]" style="background:#F7FBFD;">
      <header class="px-5 pt-4 pb-4 sticky top-0 z-30 text-white shadow-sm lg:hidden"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <div class="flex items-center justify-between gap-3">
          <div>
            <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">Suppliers</p>
            <h1 class="text-lg font-semibold text-white/95">Purchase Orders</h1>
          </div>
          <a routerLink="/purchase-orders/new" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
            <i class="ti ti-plus text-[22px]"></i>
          </a>
        </div>
      </header>

      <main class="px-4 pt-5 space-y-4 lg:px-7 lg:pt-6">
        <div class="hidden lg:flex items-center justify-between">
          <div>
            <h1 class="text-xl font-semibold text-slate-950">Purchase Orders</h1>
            <p class="text-xs text-slate-500">Create supplier orders and receive material stock.</p>
          </div>
          <a routerLink="/purchase-orders/new" class="h-10 px-4 rounded-xl bg-[#102C41] text-white text-sm font-bold flex items-center gap-2">
            <i class="ti ti-plus"></i>New PO
          </a>
        </div>

        <section class="relative">
          <i class="ti ti-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
          <input [(ngModel)]="search" (input)="loadPurchaseOrders()" placeholder="Search supplier or PO"
                 class="w-full h-11 pl-11 pr-3 bg-white border border-slate-100 rounded-2xl text-sm focus:outline-none focus:border-[#1A6FA6]">
        </section>

        <section class="grid grid-cols-2 gap-3">
          <div class="bg-white rounded-2xl border border-slate-100 p-4">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Open</p>
            <p class="text-2xl font-bold text-slate-900 mt-1">{{ openCount() }}</p>
          </div>
          <div class="bg-white rounded-2xl border border-slate-100 p-4">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Received</p>
            <p class="text-2xl font-bold text-emerald-600 mt-1">{{ receivedCount() }}</p>
          </div>
        </section>

        <section class="space-y-3 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-3 lg:space-y-0">
          @for (po of purchaseOrders(); track po.id) {
            <a [routerLink]="['/purchase-orders', po.id]" class="block bg-white rounded-2xl border border-slate-100 p-4 active:scale-[0.98] transition-all">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="font-semibold text-slate-900 truncate">{{ po.supplier_name }}</p>
                  <p class="text-[11px] text-slate-400 font-mono mt-1">{{ po.po_no }} · {{ po.order_date | date:'dd MMM yyyy' }}</p>
                  <p class="text-[11px] text-slate-500 mt-1 truncate">{{ po.model_type === 'General' ? 'General company PO' : po.model_type + ' #' + po.reference_id }}</p>
                </div>
                <div class="text-right shrink-0">
                  <p class="text-sm font-bold text-slate-900">{{ po.total | currency:'NZD':'symbol':'1.2-2' }}</p>
                  <span class="inline-flex mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border" [ngClass]="statusBadgeClass(po.status)">{{ po.status }}</span>
                </div>
              </div>
            </a>
          }
          @if (!isLoading() && purchaseOrders().length === 0) {
            <div class="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-400 lg:col-span-2 xl:col-span-3">
              <i class="ti ti-clipboard-list text-3xl"></i>
              <p class="text-sm font-semibold mt-2">No purchase orders found.</p>
            </div>
          }
        </section>
      </main>
    </div>
  `
})
export class PurchaseOrdersComponent implements OnInit {
  private api = inject(ApiService);
  purchaseOrders = signal<any[]>([]);
  isLoading = signal(false);
  search = '';

  ngOnInit() {
    this.loadPurchaseOrders();
  }

  loadPurchaseOrders() {
    this.isLoading.set(true);
    this.api.getPurchaseOrders({ search: this.search }).subscribe({
      next: (res: any) => {
        this.purchaseOrders.set(res.data?.purchase_orders || []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  openCount(): number {
    return this.purchaseOrders().filter(po => ['Draft', 'Issued', 'Partially Received'].includes(po.status)).length;
  }

  receivedCount(): number {
    return this.purchaseOrders().filter(po => po.status === 'Received').length;
  }

  statusBadgeClass(status: string): string {
    if (status === 'Received') return 'text-emerald-700 bg-emerald-50 border-emerald-100';
    if (status === 'Issued' || status === 'Partially Received') return 'text-sky-700 bg-sky-50 border-sky-100';
    if (status === 'Cancelled') return 'text-rose-700 bg-rose-50 border-rose-100';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  }
}
